// -----------------------------------------------------------------------------
// CreateChatScreen.jsx - Full-screen chat creation experience
// -----------------------------------------------------------------------------
// Replaces the chat area with an intuitive creation interface

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Paper,
  IconButton,
  Fade,
  CircularProgress,
  Container,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  PhotoCamera,
  Check,
  Palette,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useMutation } from 'react-query';
import API from '../../API';
import SelectAssetDialog from '../tools/selectfile';

const PRESET_COLORS = [
  '#1976d2', // Blue
  '#d32f2f', // Red
  '#388e3c', // Green
  '#f57c00', // Orange
  '#7b1fa2', // Purple
  '#0097a7', // Cyan
  '#c2185b', // Pink
  '#5d4037', // Brown
];

export default function CreateChatScreen({ onClose, onCreated }) {
  const theme = useTheme();
  const [displayName, setDisplayName] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [themeColor, setThemeColor] = useState(theme.palette.primary.main);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const createMutation = useMutation(
    payload => API.conversations.createConversation(payload),
    {
      onSuccess: res => {
        if (onCreated) onCreated(res.conversation_id, res);
      },
      onError: err => {
        console.error('Error creating conversation', err);
        alert(err?.response?.data?.error || err.message || 'Something went wrong');
      },
    }
  );

  const handleCreate = () => {
    if (!displayName.trim()) {
      alert('Please enter a conversation name');
      return;
    }

    const payload = {
      display_name: displayName.trim(),
      cover_image: coverImage,
      theme: { color: themeColor },
    };

    createMutation.mutate(payload);
  };

  const handleAssetPick = file => {
    setCoverImage(file.filename);
    setAssetDialogOpen(false);
  };

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#efe7dd',
          backgroundImage: theme.palette.mode === 'dark'
            ? 'none'
            : `radial-gradient(${alpha(theme.palette.common.black, 0.08)} 0.8px, transparent 0.8px)`,
          backgroundSize: '18px 18px',
          position: 'relative',
        }}
      >
        {/* Header */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 0,
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(themeColor, 0.1)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Cancel">
              <IconButton
                onClick={onClose}
                disabled={createMutation.isLoading}
              >
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" fontWeight="600">
              New Conversation
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={createMutation.isLoading ? <CircularProgress size={20} /> : <Check />}
            onClick={handleCreate}
            disabled={createMutation.isLoading || !displayName.trim()}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              bgcolor: themeColor,
              '&:hover': {
                bgcolor: themeColor,
                filter: 'brightness(0.94)',
              },
            }}
          >
            {createMutation.isLoading ? 'Creating...' : 'Create'}
          </Button>
        </Paper>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 5,
            px: 2,
          }}
        >
          <Container maxWidth="sm">
            {/* Unified Creation Card */}
            <Card
              elevation={3}
              sx={{
                borderRadius: 5,
                overflow: 'visible',
                background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
                border: `1px solid ${alpha(themeColor, 0.2)}`,
                boxShadow: `0 18px 60px ${alpha(theme.palette.common.black, 0.16)}`,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                {/* Avatar with Image Picker */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 3,
                    position: 'relative',
                  }}
                >
                  <Avatar
                    src={coverImage}
                    sx={{
                      width: 120,
                      height: 120,
                      border: `4px solid ${alpha(themeColor, 0.28)}`,
                      boxShadow: `0 8px 24px ${alpha(themeColor, 0.22)}`,
                      bgcolor: alpha(themeColor, 0.1),
                      fontSize: '3rem',
                      fontWeight: 600,
                      color: themeColor,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      },
                    }}
                    onClick={() => setAssetDialogOpen(true)}
                  >
                    {displayName ? displayName.charAt(0).toUpperCase() : '?'}
                  </Avatar>
                  
                  <Tooltip title="Change image">
                    <IconButton
                      onClick={() => setAssetDialogOpen(true)}
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 'calc(50% - 70px)',
                        bgcolor: themeColor,
                        color: 'white',
                        '&:hover': {
                          bgcolor: alpha(themeColor, 0.8),
                        },
                        boxShadow: 2,
                      }}
                      size="small"
                    >
                      <PhotoCamera fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Name Input */}
                <TextField
                  fullWidth
                  placeholder="Enter conversation name..."
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  disabled={createMutation.isLoading}
                  autoFocus
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 999,
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      bgcolor: theme.palette.background.paper,
                      '&.Mui-focused fieldset': {
                        borderColor: themeColor,
                      },
                    },
                    '& .MuiInputBase-input::placeholder': {
                      opacity: 0.65,
                    },
                    '& input': {
                      textAlign: 'center',
                    },
                  }}
                />

                {/* Color Picker - Compact */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', textAlign: 'center' }}>
                    Theme Color
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {PRESET_COLORS.map(color => (
                      <Tooltip key={color} title="Select color">
                        <IconButton
                          onClick={() => setThemeColor(color)}
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: color,
                            border: themeColor === color
                              ? `3px solid ${theme.palette.text.primary}`
                              : `2px solid ${alpha(theme.palette.common.white, 0.9)}`,
                            boxShadow: themeColor === color
                              ? `0 0 0 3px ${alpha(color, 0.18)}`
                              : `0 2px 6px ${alpha(theme.palette.common.black, 0.12)}`,
                            '&:hover': {
                              bgcolor: color,
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s',
                          }}
                        >
                          {themeColor === color && <Check sx={{ color: 'white', fontSize: '1rem' }} />}
                        </IconButton>
                      </Tooltip>
                    ))}
                    
                    <Tooltip title="Custom color">
                      <IconButton
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px dashed ${theme.palette.divider}`,
                          bgcolor: theme.palette.background.paper,
                        }}
                      >
                        <Palette fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Custom Color Picker - Inline */}
                  {showColorPicker && (
                    <Fade in>
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: alpha(theme.palette.background.default, 0.5),
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <input
                          type="color"
                          value={themeColor}
                          onChange={e => setThemeColor(e.target.value)}
                          style={{
                            width: 50,
                            height: 50,
                            border: 'none',
                            borderRadius: 8,
                            cursor: 'pointer',
                          }}
                          disabled={createMutation.isLoading}
                        />
                        <TextField
                          size="small"
                          value={themeColor}
                          onChange={e => setThemeColor(e.target.value)}
                          placeholder="#000000"
                          sx={{ width: 120 }}
                        />
                      </Box>
                    </Fade>
                  )}
                </Box>

                {/* Message Preview */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.3) : '#efe7dd',
                    backgroundImage: theme.palette.mode === 'dark'
                      ? 'none'
                      : `radial-gradient(${alpha(theme.palette.common.black, 0.08)} 0.7px, transparent 0.7px)`,
                    backgroundSize: '16px 16px',
                    borderRadius: 4,
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', textAlign: 'center' }}>
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mb: 1,
                    }}
                  >
                    <Paper
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: '18px 18px 4px 18px',
                        background: `linear-gradient(135deg, ${themeColor}, ${alpha(themeColor, 0.86)})`,
                        color: 'white',
                        maxWidth: '70%',
                        boxShadow: `0 2px 8px ${alpha(themeColor, 0.24)}`,
                      }}
                    >
                      <Typography variant="body2">
                        Hello! 👋
                      </Typography>
                    </Paper>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Paper
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: '18px 18px 18px 4px',
                        bgcolor: theme.palette.background.paper,
                        maxWidth: '70%',
                        boxShadow: `0 1px 6px ${alpha(theme.palette.common.black, 0.08)}`,
                      }}
                    >
                      <Typography variant="body2">
                        Welcome to {displayName || 'your conversation'}!
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Tip */}
            <Paper
              sx={{
                mt: 3,
                p: 2,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                💡 <strong>Tip:</strong> You can change these settings later by clicking the edit button in the conversation header.
              </Typography>
            </Paper>
          </Container>
        </Box>

        {/* Asset Picker Dialog */}
        <SelectAssetDialog
          open={assetDialogOpen}
          onClose={() => setAssetDialogOpen(false)}
          selectedAsset={null}
          onAssetClick={handleAssetPick}
        />
      </Box>
    </Fade>
  );
}
